// Soil Temperature Tool — phase → Directus product hints
//
// Maps a recommendation phase title (e.g. "Pre-emergent Window",
// "Active Growth — Fertilize") to the Directus products-collection
// categories + keyword hints to feed the shared ProductRecommendations
// component. This replaces the previous hardcoded product list which
// used amzn.to shortlinks and bypassed the curated products collection.

export interface PhaseProductHints {
  categories: string[]
  keywords: string[]
}

const FERTILIZER: PhaseProductHints['categories'] = ['Fertilizers & Soil Health']
const WEED_CONTROL: PhaseProductHints['categories'] = ['Weed Control']
const SEED: PhaseProductHints['categories'] = ['Grass Seed & Lawn Repair']
const WATERING: PhaseProductHints['categories'] = ['Watering & Irrigation']

/**
 * Pick the best products-collection categories + keyword hints for a
 * given soil-temperature recommendation phase. The keywords feed the
 * keyword-scoring layer in /api/blog/product-recommendations so phase
 * intent boosts the right products inside the chosen category.
 *
 * Returns a default of Fertilizers + the lawn-care keyword when no
 * phase matches, to ensure the ProductRecommendations component still
 * has something relevant to render.
 */
export function getProductHintsForPhase(phaseTitle: string | undefined): PhaseProductHints {
  const title = (phaseTitle || '').toLowerCase()

  if (title.includes('pre-emergent') || title.includes('pre emergent')) {
    return { categories: WEED_CONTROL, keywords: ['pre-emergent', 'crabgrass', 'preventer'] }
  }
  if (title.includes('overseed') || title.includes('seed')) {
    return { categories: SEED, keywords: ['seed', 'overseed', 'germination'] }
  }
  if (title.includes('active growth') || title.includes('peak growth') || title.includes('fertiliz')) {
    return { categories: FERTILIZER, keywords: ['fertilizer', 'lawn food', 'nitrogen'] }
  }
  if (title.includes('dormant') || title.includes('frozen') || title.includes('winter')) {
    return { categories: FERTILIZER, keywords: ['winterizer', 'fall', 'cool season'] }
  }
  if (title.includes('heat') || title.includes('stress') || title.includes('drought') || title.includes('moisture')) {
    return { categories: [...WATERING, ...FERTILIZER], keywords: ['drought', 'water', 'irrigation'] }
  }

  return { categories: FERTILIZER, keywords: ['lawn care'] }
}
